import './DonateSection.scss';
import React, { useState } from 'react';
import { DONATION_AMOUNTS } from '@/const/public/donate-page';
import { DonateTab, PaymentSystem } from '@/types/public/donate-page';
import { Currency } from '@/types/common/currency';
import { API_ROUTES } from '@/const/common/api-routes/main-api';
import { getEnvVariable } from '@/utils/functions/get-env-variable/get-env-variable';
import { Tabs } from '@/components/common/tabs/Tabs';
import { currencyToString } from '@/utils/functions/mappers/public/donate/donate';
import { useTranslation } from 'react-i18next';

export const DonateSection = () => {
    const [activeTab, setActiveTab] = useState<DonateTab>(DonateTab.oneTime);
    const [donationAmount, setDonationAmount] = useState<number>(0);
    const [currency, setCurrency] = useState<Currency>(Currency.UAH);
    const { t } = useTranslation('donatePage');

    const currencyString = currencyToString(currency);

    const handleDonateAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let inputValue = e.target.value;

        inputValue = inputValue.replace(/[^0-9]/g, '');
        inputValue = inputValue.replace(/[-+]/g, '');

        setDonationAmount(isNaN(Number(inputValue)) ? 0 : Number(inputValue));
    };

    const handleQuickAmountChange = (amount: number) => {
        setDonationAmount((donationAmount as number) + amount);
    };

    const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrency(Number(e.target.value) as Currency);
    };

    const handleSubmit: React.FormEventHandler<HTMLFormElement> | undefined = (e) => {
        e.preventDefault();

        if (donationAmount <= 0 || !Number.isInteger(donationAmount)) {
            return;
        }

        const form = e.currentTarget;

        setTimeout(() => {
            form.submit();
        }, 0);
    };

    return (
        <form
            className="donateSection"
            action={`${getEnvVariable('REACT_APP_BACKEND_URL')}/${API_ROUTES.PAYMENTS.DONATE}`}
            method="post"
            onSubmit={handleSubmit}
            data-testid="donate-section-form"
        >
            <input type="hidden" name="isSubscription" value={(activeTab === DonateTab.subscription).toString()} />
            <input type="hidden" name="paymentSystem" value={PaymentSystem.WayForPay} />
            <Tabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                tabs={[
                    { id: DonateTab.oneTime, label: t('DONATE_ONE_TIME_LABEL') },
                    { id: DonateTab.subscription, label: t('DONATE_SUBSCRIPTION_LABEL') },
                ]}
            ></Tabs>
            <div className="donateAmountSection">
                <input
                    name="amount"
                    className="donateAmountInput"
                    value={donationAmount || '0'}
                    onChange={handleDonateAmountChange}
                    placeholder="0"
                />
                <div className="currencySelector">
                    <select name="currency" value={currency} onChange={handleCurrencyChange}>
                        <option value={Currency.UAH}>{currencyToString(Currency.UAH)}</option>
                        <option value={Currency.USD}>{currencyToString(Currency.USD)}</option>
                        <option value={Currency.EUR}>{currencyToString(Currency.EUR)}</option>
                    </select>
                    <span className="arrowIcon">▼</span>
                    <span className="currencyText">{currencyString}</span>
                </div>
            </div>
            <div className="fastDonateOptionsSection">
                <button
                    className="donateFastOptionButton"
                    onClick={() => handleQuickAmountChange(DONATION_AMOUNTS[currency].small)}
                    type="button"
                >
                    <span className="donateFastValueText">+{DONATION_AMOUNTS[currency].small} </span>
                    {currencyString}
                </button>
                <button
                    className="donateFastOptionButton"
                    onClick={() => handleQuickAmountChange(DONATION_AMOUNTS[currency].medium)}
                    type="button"
                >
                    <span className="donateFastValueText">+{DONATION_AMOUNTS[currency].medium} </span>
                    {currencyString}
                </button>
                <button
                    className="donateFastOptionButton"
                    onClick={() => handleQuickAmountChange(DONATION_AMOUNTS[currency].large)}
                    type="button"
                >
                    <span className="donateFastValueText">+{DONATION_AMOUNTS[currency].large} </span>
                    {currencyString}
                </button>
            </div>
            <button className="donateButton" type="submit">
                {activeTab === DonateTab.oneTime
                    ? t('ONE_TIME_DONATE_BUTTON_LABEL')
                    : t('SUBSCRIPTION_DONATE_BUTTON_LABEL')}
            </button>
        </form>
    );
};
